import axios from 'axios';
import toast from 'react-hot-toast';
import { API_URL, isLocal } from '../config/environment';

// Konfiguracja axios - używa centralnego pliku konfiguracji

// Flaga debug - włączona tylko lokalnie
const DEBUG_API = isLocal;
const debugLog = (...args) => { if (DEBUG_API) console.log(...args); };

debugLog('🔗 API Base URL:', API_URL);

// BEZPIECZENSTWO: CSRF token przechowywany w closure modulu (niedostepny z konsoli)
let _csrfToken = null;

/**
 * Ustaw CSRF token (wywoływane z AuthContext po /auth/check)
 */
export const setCsrfToken = (token) => {
  _csrfToken = token;
};

export const getCsrfToken = () => _csrfToken;

// Device token — identyfikacja urządzenia dla systemu Trusted Devices
// Generowany raz i przechowywany w localStorage (64 znaki hex)
function _initDeviceToken() {
  const KEY = 'v3sys_device_token';
  let token = localStorage.getItem(KEY);
  if (token && /^[a-f0-9]{64}$/.test(token)) return token;
  // Generuj nowy token kryptograficznie bezpiecznym RNG
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  token = Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
  localStorage.setItem(KEY, token);
  return token;
}
const _deviceToken = _initDeviceToken();

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,  // 30 sekund (zwiekszone z 10s)
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// API z dłuższym timeoutem dla operacji takich jak finalize, raporty, KSeF
export const apiLong = axios.create({
  baseURL: API_URL,
  timeout: 60000,  // 60 sekund
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Funkcja do tworzenia API z customowym timeoutem
export const apiWithTimeout = (timeoutMs) => {
  return axios.create({
    ...api.defaults,
    timeout: timeoutMs
  });
};

// Interceptor: dołącz location_id + CSRF token do każdego requesta
api.interceptors.request.use(
  (config) => {
    // Dołącz location_id z localStorage jeśli nie jest już podany
    try {
      const saved = localStorage.getItem('selectedLocation');
      if (saved) {
        const location = JSON.parse(saved);
        if (location?.id) {
          if (!config.params) config.params = {};
          if (!config.params.location_id) {
            config.params.location_id = location.id;
          }
        }
      }
    } catch (e) {
      // Ignoruj błędy parsowania localStorage
    }

    // CSRF token dla mutujących requestów (POST/PUT/DELETE/PATCH)
    if (_csrfToken && ['post', 'put', 'delete', 'patch'].includes(config.method)) {
      config.headers['X-CSRF-Token'] = _csrfToken;
    }

    // Device token — identyfikacja urządzenia (Trusted Devices)
    if (_deviceToken) {
      config.headers['X-Device-Token'] = _deviceToken;
    }

    if (DEBUG_API) {
      const finalURL = config.baseURL + (config.url.startsWith('/') ? config.url : '/' + config.url);
      console.log('🔍 DEBUG Axios Request:', {
        baseURL: config.baseURL,
        url: config.url,
        finalURL: finalURL
      });
    }
    return config;
  }
);

// Interceptor: automatyczny retry dla 429 (rate limit) i 500/503 (błąd serwera/bazy)
//
// OPT-OUT: config.noRetry === true wyłącza ponawianie dla danego żądania.
// Używa tego polling komunikatora — ma własny, wykładniczy backoff, a retry
// z interceptora zamieniałby jeden nieudany cykl w trzy żądania do gunicorna
// (worker_class='sync', 2 workery), czyli awaria bazy zapychałaby POS.
api.interceptors.response.use(null, async (error) => {
  const config = error.config;
  const status = error.response?.status;
  if (!config || config.noRetry) return Promise.reject(error);
  config._retryCount = config._retryCount || 0;

  // Retry dla rate limit (429) i błędów serwera (500, 502, 503) — max 2 próby
  const retryStatuses = [429, 500, 502, 503];
  if (retryStatuses.includes(status) && config._retryCount < 2 && config.method === 'get') {
    config._retryCount += 1;
    const delay = config._retryCount * (status === 429 ? 1000 : 1500);
    console.warn(`[API] Retry ${config._retryCount}/2 for ${status} ${config.url} (wait ${delay}ms)`);
    await new Promise(r => setTimeout(r, delay));
    return api(config);
  }
  return Promise.reject(error);
});

// Interceptor do obsługi błędów z toast + request_id
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestId = error.response?.headers?.['x-request-id'];

    // CICHE ŻĄDANIE (config.silent === true): błąd wraca do wywołującego
    // normalnie, ale BEZ czerwonego dymka. Dla żądań w tle (polling
    // komunikatora co 3-20 s) toast z interceptora oznaczałby serię dymków
    // bez końca — także na ekranie POS, gdzie decyzja właściciela brzmi
    // "na POS cisza". Wywołujący pokazuje błąd po swojemu (pasek w panelu).
    const cichy = error.config?.silent === true;

    // Zadanie anulowane (AbortController) — cisza, bez toastu i bez "brak polaczenia"
    if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
      return Promise.reject(error);
    }

    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      console.error('🔴 Brak połączenia z serwerem backend!');
      if (!cichy) toast.error('Brak połączenia z serwerem. Sprawdź czy backend działa.');
      throw new Error('Brak połączenia z serwerem');
    }

    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.response.data?.error || 'Wystąpił błąd serwera';

      // Obsługa wygaśnięcia sesji (401)
      // NIE robimy window.location.href — to powoduje full page reload i petle.
      // Zamiast tego rzucamy error, AuthContext/ProtectedRoute ogarnie redirect w React.
      if (status === 401) {
        const authError = new Error('Sesja wygasła');
        authError.status = 401;
        authError.isAuthError = true;
        authError.requestId = requestId;
        throw authError;
      }

      // Obsługa braku uprawnień (403)
      if (status === 403) {
        const errorCode = error.response.data?.error_code;
        // Zablokowane urządzenie — twardy redirect na login (sesja cleared server-side)
        if (errorCode === 'DEVICE_BLOCKED') {
          toast.error('Urządzenie zostało zablokowane przez administratora. Wylogowano.');
          setTimeout(() => { window.location.href = '/login?reason=device_blocked'; }, 500);
          return new Promise(() => {}); // zawieś — redirect nastąpi
        }
        // Wygasle haslo — emit event do Layout (otworzy modal zmiany hasla)
        // i rzuc CICHY error (bez toast). NIE zawieszamy promise zeby finally
        // bloki contextow (setLoading=false) wykonaly sie - inaczej ProtectedRoute
        // zostaje na ekranie "Ladowanie lokalizacji" i Layout (z modalem) sie nie renderuje.
        if (errorCode === 'PASSWORD_EXPIRED') {
          if (!window.__passwordExpiredHandled) {
            window.__passwordExpiredHandled = true;
            toast('Twoje haslo wygaslo - zmien je aby kontynuowac prace', { icon: '🔑', duration: 8000 });
            window.dispatchEvent(new CustomEvent('password-expired'));
          }
          const pwdError = new Error('Haslo wygaslo - musisz je zmienic');
          pwdError.status = 403;
          pwdError.errorCode = 'PASSWORD_EXPIRED';
          pwdError.isPasswordExpired = true;
          pwdError.silent = true;  // contexty moga sprawdzic ten flag i NIE pokazywac toast
          throw pwdError;
        }
        const permError = new Error(message);
        permError.status = 403;
        permError.isPermissionError = true;
        permError.requestId = requestId;
        // Trusted Devices — przekaż error_code i device_token do AuthContext
        if (errorCode) {
          permError.errorCode = errorCode;
          permError.deviceToken = error.response.data?.device_token;
        } else {
          console.warn('🚫 Brak uprawnień:', message);
        }
        throw permError;
      }

      // Obsługa rate limit (429) — po wyczerpaniu retry
      if (status === 429) {
        console.warn('⏳ Rate limit:', message);
        const rateLimitError = new Error('Zbyt wiele zapytań. Spróbuj ponownie za chwilę.');
        rateLimitError.status = 429;
        rateLimitError.requestId = requestId;
        throw rateLimitError;
      }

      // Auto-toast dla błędów serwera (5xx) — pomijany dla żądań cichych
      if (status >= 500 && !cichy) {
        const reqIdMsg = requestId ? ` (ID: ${requestId})` : '';
        toast.error(`Błąd serwera: ${message}${reqIdMsg}`);
      }

      const err = new Error(message);
      err.requestId = requestId;
      err.status = status;
      // Przekazujemy CALE cialo odpowiedzi - bez tego kody biznesowe (409/422)
      // typu ZLY_TYP_PLIKU / CENNIK_WYMAGANY / DUPLIKAT_FAKTURY oraz pola
      // pomocnicze (wykryto, brakujace, diagnostyka) gina i caller nie odrozni
      // bledu walidacji od zwyklego bledu serwera.
      err.data = error.response.data;
      err.errorCode = error.response.data?.error_code;
      throw err;
    } else if (error.request) {
      // Timeout: request wyszedl, ale odpowiedz nie przyszla w limicie czasu.
      // Dluga operacja (np. generowanie PZ) moze wciaz trwac w tle na backendzie,
      // wiec NIE traktujemy tego jak zerwane polaczenie i propagujemy czytelny
      // komunikat + flage isTimeout (interceptor kasuje error.code, wiec caller
      // nie moze polegac na ECONNABORTED).
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || /timeout/i.test(error.message || '')) {
        const timeoutError = new Error('Przekroczono czas oczekiwania na odpowiedź serwera (timeout)');
        timeoutError.isTimeout = true;
        timeoutError.code = error.code;
        throw timeoutError;
      }
      if (!cichy) toast.error('Brak połączenia z serwerem');
      throw new Error('Brak połączenia z serwerem');
    } else {
      throw new Error('Błąd konfiguracji żądania');
    }
  }
);

// Dodaj te same interceptory do apiLong
apiLong.interceptors.request.use(api.interceptors.request.handlers[0].fulfilled);
apiLong.interceptors.response.use(null, api.interceptors.response.handlers[0].rejected);
apiLong.interceptors.response.use(
  api.interceptors.response.handlers[1].fulfilled,
  api.interceptors.response.handlers[1].rejected
);

export default api;
